import { useState, useRef, useCallback, useMemo } from "react";
import {
    Box,
    Paper,
    Typography,
    TextField,
    MenuItem,
    Chip,
    Grid,
    IconButton,
    Tooltip,
} from "@mui/material";
import { OpenInNew, Refresh } from "@mui/icons-material";
import { useNavigate } from 'react-router-dom';
import { ActivityService } from "../../service/activityService";
import { Colors } from "../../styles/theme";
import useLocation from "../../hooks/useLocation";
import EnhancedTable from "../enhancedTable/EnhancedTable";

const ACTIVITY_TYPE_LABELS = {
    customer_created: "Customer Created",
    customer_updated: "Customer Updated",
    invoice_created: "Invoice Created",
    invoice_paid: "Invoice Paid",
    appointment_booked: "Appointment Booked",
    appointment_completed: "Appointment Completed",
    prescription_created: "Prescription Created",
    payment_received: "Payment Received",
    other: "Other",
};

const getActivityColor = (activityType) => {
    switch (activityType) {
        case "customer_created":
        case "appointment_booked":
            return Colors.success;
        case "invoice_created":
        case "prescription_created":
            return Colors.info;
        case "invoice_paid":
        case "payment_received":
            return Colors.success;
        case "appointment_completed":
            return Colors.success;
        case "customer_updated":
            return Colors.primary;
        default:
            return Colors.dim_grey;
    }
};

export default function RecentActivityPage() {
    const navigate = useNavigate();
    const { selectedLocation } = useLocation();
    const [activities, setActivities] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [activityType, setActivityType] = useState("");
    const [totalRowCount, setTotalRowCount] = useState(0);
    const [pageCount, setPageCount] = useState(0);

    const abortCtrlRef = useRef(null);
    const [refreshTick, setRefreshTick] = useState(0);

    const fetchData = useCallback(({ pageSize, pageIndex /* filters, sortBy */ }) => {
        if (abortCtrlRef.current) {
            try { abortCtrlRef.current.abort(); } catch (_) { }
        }
        const ctrl = new AbortController();
        abortCtrlRef.current = ctrl;

        setIsLoading(true);
        const offset = pageSize * pageIndex;
        const params = {
            limit: pageSize,
            offset,
            activityType: activityType || undefined,
            ...(selectedLocation?.id && { locationId: selectedLocation.id })
        };

        ActivityService.getRecentActivities(params, { signal: ctrl.signal })
            .then((resp) => {
                const payload = resp?.data || { rows: [], count: 0 };
                const rows = Array.isArray(payload.rows) ? payload.rows : [];
                setActivities(rows);
                setTotalRowCount(payload.count || 0);
                setPageCount(Math.ceil((payload.count || 0) / pageSize));
            })
            .catch(() => { /* handled globally */ })
            .finally(() => setIsLoading(false));
    }, [activityType, selectedLocation]);

    const handleActivityTypeChange = (value) => {
        setActivityType(value);
    };

    const handleNavigateToCustomer = useCallback((customerId) => {
        console.log('Navigating to customer', customerId);
        navigate(`/customers/${customerId}`);
    }, [navigate]);

    return (
        <Box sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h4">
                    Recent Activity
                </Typography>
                <IconButton
                    onClick={() => setRefreshTick((s) => s + 1)}
                    disabled={isLoading}
                    title="Refresh activity"
                >
                    <Refresh />
                </IconButton>
            </Box>

            {/* Filters */}
            <Paper sx={{ p: 2, mb: 3 }}>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField
                            fullWidth
                            select
                            size="small"
                            label="Activity Type"
                            value={activityType}
                            onChange={(e) => handleActivityTypeChange(e.target.value)}
                        >
                            <MenuItem value="">All Activities</MenuItem>
                            {Object.entries(ACTIVITY_TYPE_LABELS).map(([key, label]) => (
                                <MenuItem key={key} value={key}>{label}</MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                </Grid>
            </Paper>

            {/* Activities Table (EnhancedTable) */}
            <EnhancedTable
                showStatusColumn={false}
                key={refreshTick}
                columns={useMemo(() => [
                    {
                        Header: 'Date/Time',
                        accessor: 'createdAt',
                        Cell: ({ value }) => (
                            <Typography variant="body2">{new Date(value).toLocaleString()}</Typography>
                        )
                    },
                    {
                        Header: 'Customer',
                        accessor: (row) => `${row.customer?.firstName || ''} ${row.customer?.lastName || ''}`.trim(),
                        id: 'customerName',
                        Cell: ({ row }) => (
                            <Typography
                                variant="body2"
                                sx={{ cursor: 'pointer', color: Colors.primary }}
                                onClick={() => handleNavigateToCustomer(row.original.customerId)}
                            >
                                {row.original.customer?.firstName} {row.original.customer?.lastName}
                            </Typography>
                        )
                    },
                    {
                        Header: 'Activity',
                        accessor: 'activityType',
                        Cell: ({ value }) => (
                            <Chip
                                label={ACTIVITY_TYPE_LABELS[value] || value}
                                size="small"
                                sx={{ background: getActivityColor(value), color: Colors.white }}
                            />
                        )
                    },
                    {
                        Header: 'Description',
                        accessor: 'description',
                        Cell: ({ value }) => (
                            <Typography variant="body2" sx={{ maxWidth: 250 }}>{value || '-'}</Typography>
                        )
                    },
                    {
                        Header: 'Performed By',
                        accessor: (row) => row.performedByUser ? `${row.performedByUser.firstName} ${row.performedByUser.lastName}` : 'System',
                        id: 'performedBy'
                    },
                    {
                        Header: 'Location',
                        accessor: (row) => row.location?.name || '-',
                        id: 'locationId',
                    }
                ], [handleNavigateToCustomer])}
                data={activities}
                totalRowCount={totalRowCount}
                pageCount={pageCount}
                fetchData={fetchData}
                loading={isLoading}
                selectionMode="none"
                actions={[
                    {
                        icon: (
                            <Tooltip title="View Customer">
                                <OpenInNew fontSize="small" />
                            </Tooltip>
                        ),
                        onClick: (row) => handleNavigateToCustomer(row.customer.id)
                    }
                ]}
            />
        </Box>
    );
}
